import classes from './Modal.module.css';
import {
  useContext,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import ReactDOM from 'react-dom';

// IMPORT OWN COMPONENTS
import Backdrop from './Backdrop';
import ModalContext from '../../store/modal-context';

const ModalOverlay = forwardRef((props, ref) => {
  const header = props.content.header;
  const heading = props.content.heading;
  const subheading = props.content.subheading;
  const body = props.content.body;
  const show = props.show;
  const headerRef = useRef();
  const bodyRef = useRef();
  const wrapperRef = useRef();
  const innerBodyRef = useRef();
  const [fullHeight, setFullHeight] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(60);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);

  function recheckHeight() {
    if (props.noScroll) {
      setFullHeight(true);
    } else {
      if (bodyRef.current.scrollHeight > bodyRef.current.clientHeight) {
        setFullHeight(true);
      } else if (
        bodyRef.current.scrollHeightHeight < bodyRef.current.clientHeight
      ) {
        setFullHeight(false);
      } else if (
        bodyRef.current.scrollHeight - innerBodyRef.current.scrollHeight >
        headerRef.current.clientHeight + 60
      ) {
        setFullHeight(false);
      }
    }
  }

  const updateHeight = () => {
    recheckHeight();
  };

  useImperativeHandle(ref, () => ({
    updateHeight,
  }));

  useEffect(() => {
    if (show) recheckHeight();
    else setFullHeight(false);
  }, [show, scrollHeight, clientHeight, bodyRef.current]);

  useEffect(() => {
    const currentRef = bodyRef.current;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setScrollHeight(entry.target.scrollHeight);
      });
    });

    observer.observe(currentRef);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const currentRef = bodyRef.current;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setClientHeight(entry.target.clientHeight);
      });
    });

    observer.observe(currentRef);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const currentRef = innerBodyRef.current;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        recheckHeight();
      });
    });

    observer.observe(currentRef);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      className={`${classes.modalWrapper} ${show && classes.active}`}
      ref={wrapperRef}
    >
      <div
        className={`${classes.modal} ${show && classes.active} ${
          fullHeight && !props.noScroll && classes.scroll
        }`}
        ref={bodyRef}
        style={{ height: props.noScroll && '80vh' }}
      >
        <svg
          className="absolute top-[15px] right-[15px] h-6 w-6 text-[var(--text-color)]"
          onClick={props.onHide}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" />{' '}
          <line x1="18" y1="6" x2="6" y2="18" />{' '}
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
        <div
          className={`${props.noScroll ? 'h-[80vh]' : 'h-full'} w-full p-0 m-0`}
        >
          <div className={classes.header} ref={headerRef}>
            <div>
              <span>{header}</span>
              {heading && (
                <span className={`text-[26px] uppercase text-primary`}>
                  {heading}
                </span>
              )}
              {subheading && (
                <>
                  <br />
                  <span className="uppercase font-[400] text-[20px] text-[var(--text-color)]">
                    {subheading}
                  </span>
                </>
              )}
            </div>
          </div>
          <div
            className={classes.body}
            onLoad={() => {
              setHeaderHeight(headerRef.current.scrollHeight);
            }}
            style={{
              height: 'calc(100% - 20px - ' + headerHeight + 'px)',
            }}
          >
            <div
              ref={innerBodyRef}
              style={{
                height:
                  props.noScroll && 'calc(100%  - ' + headerHeight + 'px)',
              }}
            >
              {body}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export const CModal = forwardRef((props, ref) => {
  const { heading, subheading, body, noScroll, closeModal } =
    useContext(ModalContext);
  const content = { heading, subheading, body };
  const modalRef = useRef();

  const updateHeight = () => {
    modalRef.current.updateHeight();
  };

  useImperativeHandle(ref, () => ({
    updateHeight,
  }));

  useEffect(() => {
    if (!!content.heading) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [content.heading]);

  if (content == null) return null;
  return (
    <div style={{ height: '100%' }}>
      <Backdrop show={!!content.heading} onClick={closeModal} />
      {ReactDOM.createPortal(
        <ModalOverlay
          content={content}
          show={!!content.heading}
          onHide={closeModal}
          ref={modalRef}
          noScroll={noScroll}
        />,
        document.getElementById('overlay-root')
      )}
    </div>
  );
});
